import type { UpdaterRollout as UpdaterData, UpdaterStep } from '../../api/settingsAgentReleases'

/**
 * Agent releases pe o instalare tinuta de updater-ul on-prem: ce versiune de
 * agent primeste fiecare companie, pe fiecare sistem. NUMAI CITIRE - o
 * stabileste updater-ul (`shared/agent_rollout.json`), iar API-ul o aplica la
 * fiecare intrebare a agentului. Publicarea si Revert de mai jos nu schimba
 * ce primesc aparatele cat timp regulile updater-ului sunt scrise.
 */

const OS_LABELS: Record<string, string> = {
  windows: 'Windows 10/11',
  winserver: 'Windows Server',
  linux: 'Linux',
  mac: 'macOS',
}

const KIND_LABELS: Record<UpdaterStep['kind'], string> = {
  company: 'company rule',
  sp: 'service provider rule',
  infrastructure: 'installation rule',
}

/**
 * Celula unei companii pe un sistem, in cuvinte. Regulile vin in ordinea in
 * care le incearca API-ul; dupa prima cu 100% nu mai ajunge niciun aparat.
 *
 *   []                                -> "No update"
 *   [26.10.12.1 100%]                 -> "26.10.12.1"
 *   [26.10.12.1 25%, 26.10.5.1 100%]  -> "26.10.12.1 on 25% of devices, the rest 26.10.5.1"
 *   [26.10.12.1 25%]                  -> "26.10.12.1 on 25% of devices, the rest no update"
 */
export function rolloutText(steps: UpdaterStep[]): string {
  if (steps.length === 0) return 'No update'

  const parts: string[] = []

  for (const step of steps) {
    if (step.percent <= 0) continue

    const lead = parts.length === 0 ? '' : 'the rest '

    if (step.percent >= 100) {
      parts.push(lead + step.version)
      return parts.join(', ')
    }

    parts.push(`${lead}${step.version} on ${step.percent}% of devices`)
  }

  return parts.length === 0 ? 'No update' : `${parts.join(', ')}, the rest no update`
}

/** Felul regulii care da versiunea principala, pentru `title`. */
function ruleOf(steps: UpdaterStep[]): string {
  const first = steps.find((step) => step.percent > 0)
  return first === undefined ? '' : KIND_LABELS[first.kind]
}

export function UpdaterRollout({ updater }: { updater: UpdaterData }) {
  return (
    <div className="portlet light bordered od-ar-updater" id="odArUpdater">
      <div className="portlet-title">
        <div className="caption">
          <i className="fas fa-lock" aria-hidden="true"></i> Agent versions per company{' '}
          <span className="label label-default">Set by the on-prem updater</span>
        </div>
      </div>
      <div className="portlet-body">
        <p className="text-muted">
          Read only. The updater decides which agent version each company receives; publishing or reverting on this
          page does not change it while the updater rules are in place. A percentage is applied per device and never
          changes for the same device and version.
        </p>
        {updater.error !== '' && (
          <div className="alert alert-warning" role="alert">
            <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> {updater.error}
          </div>
        )}
        {updater.companies.length === 0 ? (
          <p className="text-muted">No active company.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-condensed od-ar-updater-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Service provider</th>
                  {updater.os.map((os) => (
                    <th key={os}>{OS_LABELS[os] ?? os}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {updater.companies.map((row) => (
                  <tr key={row.companyId} data-company={row.companyId}>
                    <td>{row.company}</td>
                    <td>{row.sp}</td>
                    {updater.os.map((os) => {
                      const steps = row.os[os] ?? []
                      return (
                        <td key={os} title={ruleOf(steps)}>
                          {rolloutText(steps)}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
